import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import SmallButton from "@ats/src/components/shared/SmallButton";
import ManageBillingActions from "@ats/src/views/accountAdmin/accountBilling/ManageBillingActions";

export interface AiCreditTier {
  name: string;
  credits: number;
  priceDollars: number;
  lookupKey: string;
  priceId: string;
  buttonText: string;
  buttonType?: "primary" | "secondary";
  isBestValue?: boolean;
}

interface AiSubscriptionTierCardProps {
  tier: AiCreditTier;
  isCurrentPlan: boolean;
  hasActiveSubscription: boolean;
  cancelAtPeriodEnd?: boolean | null;
  isLoading?: boolean;
  isLoadingButton?: boolean;
  isCanceling?: boolean;
  onSelect: (tier: AiCreditTier) => void;
  onCreateNewSubscription: (tier: AiCreditTier) => void;
  onClickCancel: () => void;
}

const AiSubscriptionTierCard: React.FC<AiSubscriptionTierCardProps> = ({
  tier,
  isCurrentPlan,
  hasActiveSubscription,
  cancelAtPeriodEnd,
  isLoading = false,
  isLoadingButton = false,
  isCanceling = false,
  onSelect,
  onCreateNewSubscription,
  onClickCancel,
}) => {
  // Mirrors the analog's handleOnClickSubscriptionAction (PlanCard.tsx:98-103):
  // the button action forks on hasActiveSubscription — change an existing
  // subscription vs. create a brand-new one.
  const handleOnClickSubscriptionAction = () => {
    if (hasActiveSubscription) {
      onSelect(tier);
    } else {
      onCreateNewSubscription(tier);
    }
  };

  return (
    <Styled.Wrapper data-testid={`ai-tier-card-${tier.lookupKey}`}>
      <Styled.Content>
        <Styled.Title>
          <h3>{tier.name}</h3>
          {isCurrentPlan ? (
            <Styled.BestValueBadge>Current plan</Styled.BestValueBadge>
          ) : (
            tier.isBestValue && <Styled.BestValueBadge>Best value</Styled.BestValueBadge>
          )}
        </Styled.Title>

        <Styled.Price>
          <h4>
            ${tier.priceDollars}
            <span>per month</span>
          </h4>
        </Styled.Price>

        <Styled.Credits>
          <strong>{tier.credits.toLocaleString()} credits</strong>
          <span>per month</span>
        </Styled.Credits>
      </Styled.Content>

      <Styled.Buttons>
        {isCurrentPlan ? (
          cancelAtPeriodEnd ? (
            <Styled.ManageBillingContainer>
              <ManageBillingActions
                hasActiveSubscription={hasActiveSubscription}
                returnUrl="/hire/settings/plato-ai/billing"
              />
            </Styled.ManageBillingContainer>
          ) : (
            <SmallButton onClick={onClickCancel} disabled={isCanceling} styleType="secondary">
              Cancel
            </SmallButton>
          )
        ) : (
          <Styled.Button
            onClick={handleOnClickSubscriptionAction}
            loading={isLoadingButton}
            disabled={isLoading}
            styleType={tier.buttonType || "secondary"}
          >
            {tier.buttonText}
          </Styled.Button>
        )}
      </Styled.Buttons>
    </Styled.Wrapper>
  );
};

export default AiSubscriptionTierCard;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Wrapper = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_Wrapper;
    ${[t.p(5), t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
    position: relative;
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    flex-grow: 1;
    min-width: 200px;
    max-width: 224px;
  `;
});

Styled.Content = styled.div`
  label: AiSubscriptionTierCard_Content;
  display: flex;
  flex-direction: column;
`;

Styled.Title = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_Title;
    display: flex;
    align-items: center;
    flex-direction: row;
    ${[t.mb(4)]}

    h3 {
      ${[t.text.bold, t.text.sm, t.h(5)]}
      line-height: 1.3;
    }
  `;
});

Styled.Price = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_Price;
    display: flex;
    align-items: center;
    color: ${t.dark ? t.color.gray[200] : t.color.black};

    h4 {
      ${[t.text.bold]}
      font-size: 2rem;
    }

    span {
      display: inline-block;
      ${[t.text.normal, t.text.xs, t.ml(1)]}
      line-height: 1.4;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});

Styled.Credits = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_Credits;
    ${[t.mt(4)]}
    display: flex;
    flex-direction: column;

    strong {
      ${[t.text.bold, t.text.base]}
      color: ${t.dark ? t.color.gray[100] : t.color.black};
    }
    span {
      ${[t.text.xs, t.mt("px")]}
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});

Styled.Buttons = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_Buttons;
    display: flex;
    ${[t.mt(5)]}
  `;
});

Styled.ManageBillingContainer = styled.div`
  label: AiSubscriptionTierCard_ManageBillingContainer;
  display: flex;
  align-items: center;

  > div {
    width: auto;
    align-items: center;

    > div {
      margin-top: 0;
    }
  }
`;

Styled.Button = styled(SmallButton)`
  label: AiSubscriptionTierCard_Button;
  min-width: 82px;
`;

Styled.CurrentTag = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_CurrentTag;
    ${[t.px(2), t.text.xs]}
    display: inline-flex;
    align-items: center;
    height: 29px;
    border-radius: 5px;
    border: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[300]};
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    background: transparent;
  `;
});

Styled.BestValueBadge = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_BestValueBadge;
    ${[t.ml(2), t.px(2)]}
    font-size: 0.625rem;
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    background: ${t.dark ? t.color.gray[700] : "linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%)"};
    flex-shrink: 0;
    display: flex;
    align-items: center;
    line-height: 1.3;
    height: 19px;
    border-radius: 5px;
  `;
});

Styled.SavingsBadge = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionTierCard_SavingsBadge;
    ${[t.ml(1), t.px(2)]}
    font-size: 0.625rem;
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    background: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    flex-shrink: 0;
    display: flex;
    align-items: center;
    line-height: 1.3;
    height: 19px;
    border-radius: 5px;
  `;
});
